// VisitApprovalsPage.js
// Visitor Management – Bulk Approvals UI (Host-scoped, backend-connected)

import React, {
  useState,
  useEffect,
  useContext,
  useCallback,
  useMemo,
} from "react";
import { useNavigate } from "react-router-dom";
import axiosClient from "../../../api/axiosClient";
import { AuthContext } from "../../../context/AuthContext";

function VisitApprovalsPage() {
  const navigate = useNavigate();
  const { user, activeScope } = useContext(AuthContext);

  const tenantId = activeScope?.tenant_id;
  const locationId = activeScope?.location_id;
  const currentHostName = user?.username || "";

  // -----------------------------
  // State
  // -----------------------------
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(20);

  const [statusFilter, setStatusFilter] = useState("PENDING");
  const [searchText, setSearchText] = useState("");

  const [selectedIds, setSelectedIds] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const hasSelection = selectedIds.length > 0;
  const scopeMissing = !tenantId || !locationId;

  // -----------------------------
  // Helpers - SAFE mapping
  // -----------------------------
  const mapRowsFromApi = useCallback((apiRows) => {
    return (apiRows || []).map((r) => ({
      id: r.id ?? r.visit_id,
      visitorName: r.visitorName || r.visitor_name,
      company: r.company,
      hostName: r.hostName || r.host_name,
      date: r.date,
      time: r.time,
      status: r.status,
    }));
  }, []);

  const statusBadge = (status) => {
    switch ((status || "").toUpperCase()) {
      case "PENDING":
        return <span className="badge bg-warning text-dark">Pending</span>;
      case "APPROVED":
        return <span className="badge bg-success">Approved</span>;
      case "REJECTED":
        return <span className="badge bg-danger">Rejected</span>;
      default:
        return <span className="badge bg-secondary">{status}</span>;
    }
  };

  const isSelected = (id) => selectedIds.includes(id);

  const toggleRow = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const toggleSelectAll = (checked, visibleIds) => {
    if (checked) {
      setSelectedIds((prev) => Array.from(new Set([...prev, ...visibleIds])));
    } else {
      setSelectedIds((prev) => prev.filter((id) => !visibleIds.includes(id)));
    }
  };

  // -----------------------------
  // Fetch visits (host-scoped)
  // -----------------------------
  const fetchVisits = useCallback(
    async (pageToLoad = page) => {
      if (scopeMissing) {
        setError("Tenant / Location missing in scope.");
        setRows([]);
        return;
      }

      if (!currentHostName) {
        setError("Logged-in user name missing in AuthContext.");
        setRows([]);
        return;
      }

      setLoading(true);
      setError(null);

      try {
        const res = await axiosClient.get(
          "/visitor-management/reports/visits",
          {
            params: {
              tenant_id: tenantId,
              location_id: locationId,
              status: statusFilter === "ALL" ? undefined : statusFilter,
              host: currentHostName,
              search: searchText || undefined,
              page: pageToLoad,
              page_size: pageSize,
            },
          }
        );

        const data = res.data?.data || {};
        const mapped = mapRowsFromApi(data.rows || []);

        setRows(mapped);
        setTotal(data.total || mapped.length);
        setPage(pageToLoad);
      } catch (err) {
        console.error("❌ fetchVisits error:", err);
        setError("Failed to load visit approvals.");
        setRows([]);
      } finally {
        setLoading(false);
      }
    },
    [
      tenantId,
      locationId,
      statusFilter,
      searchText,
      currentHostName,
      scopeMissing,
      page,
      pageSize,
      mapRowsFromApi,
    ]
  );

  useEffect(() => {
    fetchVisits(1);
  }, [statusFilter, searchText]);

  // -----------------------------
  // Visible Rows
  // -----------------------------
  const visibleRows = useMemo(() => {
    return rows.filter(
      (r) => !currentHostName || !r.hostName || r.hostName === currentHostName
    );
  }, [rows, currentHostName]);

  const visibleIds = visibleRows.map((r) => r.id);
  const allVisibleSelected =
    visibleIds.length > 0 && visibleIds.every((id) => selectedIds.includes(id));

  // -----------------------------
  // BULK Approve
  // -----------------------------
  const bulkApprove = async () => {
    if (!hasSelection) return;

    if (!window.confirm(`Approve ${selectedIds.length} visits?`)) return;

    let success = 0;
    let failed = 0;

    for (const id of selectedIds) {
      try {
        await axiosClient.post(`/visitor-management/visits/${id}/approve`, {
          comment: "",
        });
        success++;
      } catch (e) {
        failed++;
      }
    }

    alert(`Bulk Approve Completed\nSuccess: ${success}\nFailed: ${failed}`);

    setSelectedIds([]);
    fetchVisits();
  };

  // -----------------------------
  // BULK Reject
  // -----------------------------
  const bulkReject = async () => {
    if (!hasSelection) return;

    const reason = window.prompt("Enter rejection reason:");
    if (!reason) return;

    let success = 0;
    let failed = 0;

    for (const id of selectedIds) {
      try {
        await axiosClient.post(`/visitor-management/visits/${id}/reject`, {
          reason,
        });
        success++;
      } catch (e) {
        failed++;
      }
    }

    alert(`Bulk Reject Completed\nSuccess: ${success}\nFailed: ${failed}`);

    setSelectedIds([]);
    fetchVisits();
  };

  // -----------------------------
  // Render
  // -----------------------------
  return (
    <div className="container-fluid">
      {/* Header */}
      <div
        className="mb-4 px-4 py-3 text-white d-flex justify-content-between align-items-center"
        style={{
          borderRadius: "18px",
          background:
            "linear-gradient(90deg, #3f51b5 0%, #5c6bc0 40%, #2196f3 100%)",
        }}
      >
        <div>
          <h3 className="fw-bold mb-0">Visit Approvals</h3>
          <small>Manage visitor approvals</small>
        </div>

        <div className="d-flex gap-2">
          <button
            className="btn btn-success btn-sm"
            disabled={!hasSelection}
            onClick={bulkApprove}
          >
            Approve Selected
          </button>
          <button
            className="btn btn-danger btn-sm"
            disabled={!hasSelection}
            onClick={bulkReject}
          >
            Reject Selected
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="card shadow-sm mb-3" style={{ borderRadius: "18px" }}>
        <div className="card-body d-flex flex-wrap gap-3">
          <div>
            <label className="form-label small">Status</label>
            <select
              className="form-select form-select-sm"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <option value="ALL">All</option>
              <option value="PENDING">Pending</option>
              <option value="APPROVED">Approved</option>
              <option value="REJECTED">Rejected</option>
            </select>
          </div>

          <div>
            <label className="form-label small">Search</label>
            <input
              className="form-control form-control-sm"
              placeholder="Search visitor/company..."
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
            />
          </div>
        </div>
      </div>

      {/* Error */}
      {error && <div className="alert alert-danger">{error}</div>}

      {/* Table */}
      <div className="card shadow-sm" style={{ borderRadius: "18px" }}>
        <div className="card-body p-0">
          {loading ? (
            <div className="p-3">Loading...</div>
          ) : (
            <div className="table-responsive">
              <table className="table table-hover mb-0 align-middle">
                <thead className="table-light">
                  <tr>
                    <th>
                      <input
                        type="checkbox"
                        checked={allVisibleSelected}
                        onChange={(e) =>
                          toggleSelectAll(e.target.checked, visibleIds)
                        }
                      />
                    </th>
                    <th>Visitor</th>
                    <th>Company</th>
                    <th>Host</th>
                    <th>Date / Time</th>
                    <th>Status</th>
                    <th>View</th>
                  </tr>
                </thead>
                <tbody>
                  {visibleRows.map((r) => {
                    const visitId = r.id ?? r.visit_id;

                    return (
                      <tr key={visitId}>
                        <td>
                          <input
                            type="checkbox"
                            checked={isSelected(visitId)}
                            onChange={() => toggleRow(visitId)}
                          />
                        </td>
                        <td>{r.visitorName}</td>
                        <td>{r.company}</td>
                        <td>{r.hostName}</td>
                        <td>
                          {r.date} • {r.time}
                        </td>
                        <td>{statusBadge(r.status)}</td>
                        <td>
                          <button
                            className="btn btn-sm btn-outline-primary"
                            onClick={() =>
                              navigate(
                                `/visitor/approvals/${visitId}?source=approvals`
                              )
                            }
                          >
                            View
                          </button>
                        </td>
                      </tr>
                    );
                  })}

                  {!loading && visibleRows.length === 0 && (
                    <tr>
                      <td colSpan="7" className="text-center py-3 text-muted">
                        No visits found.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}

          <div className="px-3 py-2 small text-muted d-flex justify-content-between">
            <span>
              Showing {visibleRows.length} of {total}
            </span>
            <span>Page {page}</span>
          </div>
        </div>
      </div>
    </div>
  );
}

export default VisitApprovalsPage;
